import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, X, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-only dialog to grant / revoke organizer access on an event.
 * Schema already supports multi-user grants with roles — the UI shows a
 * single "role" select so the owner can later wire MANAGER / VIEWER without
 * touching the API.
 */
export default function AssignOrganizerDialog({ open, onOpenChange, event, token, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null);
  const [role, setRole] = useState('owner');
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!event?.id || !token) return;
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        axios.get(`${API}/events/${event.id}/organizers`, { headers }),
        axios.get(`${API}/organizer-candidates`, { headers }),
      ]);
      setAssignees(a.data?.organizers || []);
      setCandidates(c.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, token]);

  useEffect(() => {
    if (open) {
      setPicked(null);
      setSearch('');
      setRole('owner');
      load();
    }
  }, [open, load]);

  const filtered = candidates
    .filter((c) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
    })
    .filter((c) => !assignees.find((a) => a.user_id === c.id))
    .slice(0, 50);

  const handleAssign = async () => {
    if (!picked) {
      toast.error('Pick a user first');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/events/${event.id}/organizers`,
        { user_id: picked.id, role, permissions: [] },
        { headers },
      );
      toast.success(`Assigned ${picked.name || picked.email} as ${role.toUpperCase()}`);
      setPicked(null);
      setSearch('');
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (userId) => {
    if (!window.confirm('Revoke this organizer\'s access to the event?')) return;
    try {
      await axios.delete(`${API}/events/${event.id}/organizers/${userId}`, { headers });
      toast.success('Access revoked');
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-zinc-800 text-white w-[calc(100vw-1.5rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        data-testid="assign-organizer-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-['Josefin_Sans'] text-xl">ASSIGN ORGANIZER</DialogTitle>
          <p className="text-zinc-400 text-xs mt-1 font-mono">
            Event: <span className="text-white">{event?.title}</span>
          </p>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10 text-zinc-500 text-xs font-mono">
            <Loader2 size={16} className="animate-spin mr-2" /> LOADING…
          </div>
        )}

        {!loading && (
          <div className="space-y-6 mt-2">
            {/* Current assignees */}
            <section data-testid="current-assignees-section">
              <h3 className="font-mono text-xs text-zinc-400 tracking-widest mb-2">
                CURRENT ACCESS · {assignees.length}
              </h3>
              {assignees.length === 0 ? (
                <p className="text-zinc-500 text-xs">No one assigned to this event yet.</p>
              ) : (
                <div className="space-y-2">
                  {assignees.map((a) => (
                    <div
                      key={a.user_id}
                      className="bg-[#050505] border border-zinc-800 p-3 flex items-center justify-between gap-3"
                      data-testid={`assignee-row-${a.user_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-sm font-['Josefin_Sans']">{a.name || 'Unknown'}</span>
                          <span className={`px-1.5 py-0.5 text-[10px] font-mono tracking-wider border ${
                            a.role === 'owner'
                              ? 'bg-[#B5FF00]/10 text-[#B5FF00] border-[#B5FF00]/30'
                              : a.role === 'manager'
                              ? 'bg-blue-500/10 text-blue-400 border-blue-500/30'
                              : 'bg-zinc-700 text-zinc-300 border-zinc-600'
                          }`}>
                            {String(a.role || 'owner').toUpperCase()}
                          </span>
                          {a.is_primary && (
                            <span className="px-1.5 py-0.5 text-[10px] font-mono bg-amber-500/10 text-amber-400 border border-amber-500/30 tracking-wider">
                              PRIMARY
                            </span>
                          )}
                        </div>
                        <div className="text-zinc-500 text-xs font-mono mt-0.5 truncate">{a.email}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRevoke(a.user_id)}
                        className="text-zinc-500 hover:text-red-500 p-1"
                        data-testid={`revoke-${a.user_id}`}
                        title="Revoke"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Add new assignee */}
            <section data-testid="add-assignee-section">
              <h3 className="font-mono text-xs text-zinc-400 tracking-widest mb-2">GRANT NEW ACCESS</h3>
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPicked(null); }}
                placeholder="Search by name or email…"
                className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none mb-2"
                data-testid="assignee-search-input"
              />
              <div className="max-h-48 overflow-y-auto border border-zinc-800 divide-y divide-zinc-900">
                {filtered.length === 0 && (
                  <p className="text-zinc-500 text-xs p-3">No matches. Users must have an account first.</p>
                )}
                {filtered.map((c) => {
                  const sel = picked?.id === c.id;
                  return (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => setPicked(c)}
                      className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-[#050505] ${
                        sel ? 'bg-[#B5FF00]/10' : ''
                      }`}
                      data-testid={`candidate-${c.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-['Josefin_Sans']">{c.name || '(no name)'}</div>
                        <div className="text-zinc-500 text-xs font-mono truncate">{c.email}</div>
                      </div>
                      {!c.is_organizer && (
                        <span className="px-1.5 py-0.5 text-[10px] font-mono bg-zinc-800 text-zinc-400 border border-zinc-700 tracking-wider ml-2">
                          WILL BE PROMOTED
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {picked && (
                <div className="mt-3 bg-[#050505] border border-zinc-800 p-3 space-y-2" data-testid="pick-config">
                  <div className="text-sm text-white font-['Josefin_Sans']">{picked.name} <span className="text-zinc-500 text-xs">· {picked.email}</span></div>
                  <div className="flex items-center gap-3">
                    <label className="font-mono text-[11px] text-zinc-400 tracking-widest">ROLE</label>
                    <select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="h-9 bg-[#0A0A0A] border border-zinc-800 text-white px-2 text-sm focus:border-[#B5FF00] focus:outline-none"
                      data-testid="role-select"
                    >
                      <option value="owner">OWNER</option>
                      <option value="manager">MANAGER</option>
                      <option value="viewer">VIEWER</option>
                    </select>
                    <button
                      type="button"
                      onClick={handleAssign}
                      disabled={saving}
                      className="btn-primary px-3 h-9 text-xs flex items-center gap-1 ml-auto"
                      data-testid="confirm-assign-btn"
                    >
                      {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                      GRANT
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
